import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
// Pressed states: Tailwind only applies hover:* where the device can hover, and
// preflight removes the native tap highlight, so without these a tap on a
// touch screen gave no feedback at all until the network came back. Gated to
// devices with a touch pointer so mouse-only desktops look exactly as before.
const buttonVariants = cva('inline-flex cursor-pointer items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50', {
    variants: {
        variant: {
            default: 'bg-primary text-primary-foreground hover:bg-primary/90 any-pointer-coarse:active:bg-primary/80',
            destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 any-pointer-coarse:active:bg-destructive/80',
            outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground any-pointer-coarse:active:bg-accent any-pointer-coarse:active:text-accent-foreground',
            secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80 any-pointer-coarse:active:bg-secondary/70',
            ghost: 'hover:bg-accent hover:text-accent-foreground any-pointer-coarse:active:bg-accent any-pointer-coarse:active:text-accent-foreground',
            link: 'text-primary underline-offset-4 hover:underline any-pointer-coarse:active:opacity-70',
        },
        size: {
            default: 'h-10 px-4 py-2',
            sm: 'h-9 rounded-md px-3',
            lg: 'h-11 rounded-md px-8',
            icon: 'h-10 w-10',
        },
    },
    defaultVariants: {
        variant: 'default',
        size: 'default',
    },
});
/**
 * The one size under 40px grows to 40px where the primary pointer is a finger.
 * Only when the caller kept the variant's height: a caller that sets its own
 * (`h-7`, `size-8`) has sized the button for its surface and owns its touch
 * size too, so a dense toolbar is never reflowed from here.
 */
const COARSE_SIZE = { sm: 'pointer-coarse:h-10' };
const OWN_HEIGHT = /(?:^|\s)(?:h|size)-\S/;
function coarseSizeClass(size, className) {
    if (className && OWN_HEIGHT.test(className))
        return undefined;
    return COARSE_SIZE[size ?? 'default'];
}
function Button({ className, variant, size, asChild = false, loading, loadingText, children, ref, ...props }) {
    const classes = cn(buttonVariants({ variant, size }), coarseSizeClass(size, className), className);
    if (asChild) {
        return (_jsx(Slot, { className: classes, ref: ref, ...props, children: children }));
    }
    return (_jsxs("button", { className: classes, ref: ref, disabled: loading || props.disabled, ...props, children: [loading && _jsx(Loader2, { className: "mr-2 h-4 w-4 animate-spin" }), loading && loadingText ? loadingText : children] }));
}
export { Button };
